import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { BubbleBarComponent, BubbleItem } from '../bubble-bar/bubble-bar.component';
import { NavigationService } from '../../services/navigation.service';
import { CartService } from '../../services/cart.service';
import { StorageService } from '../../services/storage.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, BubbleBarComponent],
  template: `
    <div class="bottom-nav-wrapper" #navWrapper>
      <app-bubble-bar
        #bubbleBar
        [items]="navItems"
        [activeItemId]="activeItemId"
        [enableReorder]="false"
        [enableHaptics]="true"
        (itemClick)="onItemClick($event)"
      ></app-bubble-bar>

      @if (cartItemCount > 0 && hasCartItem()) {
        <div class="cart-badge-overlay" [style.left.px]="badgeLeftPosition" [style.top.px]="badgeTopPosition">
          <div class="cart-badge" [attr.data-count]="cartItemCount">
            {{ cartItemCount > 99 ? '99+' : cartItemCount }}
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      position: fixed;
      bottom: 12px;
      left: 0;
      right: 0;
      z-index: 1000;
      pointer-events: none;
    }

    .bottom-nav-wrapper {
      position: relative;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    app-bubble-bar {
      pointer-events: auto;
      width: 100%;
    }

    .cart-badge-overlay {
      position: absolute;
      pointer-events: none;
      z-index: 1001;
      transition: left 300ms cubic-bezier(0.4, 0, 0.2, 1), top 300ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .cart-badge {
      background: var(--ion-color-primary, #DE0F17);
      color: white;
      padding: 4px 8px;
      font-size: 11px;
      font-weight: 800;
      border-radius: 12px;
      min-width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(222, 15, 23, 0.4), 0 0 0 2px white;

      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
      letter-spacing: -0.2px;

      animation: badge-appear 400ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    @keyframes badge-appear {
      0% {
        opacity: 0;
        transform: scale(0.3);
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    @media (max-width: 576px) {
      :host {
        bottom: 8px;
      }

      .cart-badge-overlay {
        top: -6px;
      }

      .cart-badge {
        font-size: 10px;
        min-width: 22px;
        height: 22px;
        padding: 3px 6px;
      }
    }

  `]
})
export class BottomNavComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('bubbleBar', { read: BubbleBarComponent }) bubbleBar?: BubbleBarComponent;
  @ViewChild('navWrapper', { read: ElementRef }) navWrapper?: ElementRef;

  navItems: BubbleItem[] = [];
  activeItemId: string | null = null;
  cartItemCount = 0;
  badgeLeftPosition = 0;
  badgeTopPosition = 0;

  private cartSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private authSubscription?: Subscription;

  // Items par défaut (non connecté)
  private readonly defaultItems: BubbleItem[] = [
    { id: 'home', icon: 'home', label: 'Accueil', order: 0 },
    { id: 'magasins', icon: 'storefront', label: 'Magasins', order: 1 },
    { id: 'more', icon: 'ellipsis-horizontal', label: 'Plus', order: 2 },
    { id: 'login', icon: 'log-in', label: 'Connexion', order: 3 },
  ];

  // Items pour utilisateur connecté
  private readonly authenticatedItems: BubbleItem[] = [
    { id: 'home', icon: 'home', label: 'Accueil', order: 0 },
    { id: 'products', icon: 'gift', label: 'Dons', order: 1 },
    { id: 'magasins', icon: 'storefront', label: 'Magasins', order: 2 },
    { id: 'cart', icon: 'cart', label: 'Panier', order: 3 },
    { id: 'profile', icon: 'person', label: 'Profil', order: 4 },
    { id: 'more', icon: 'ellipsis-horizontal', label: 'Plus', order: 5 },
  ];

  constructor(
    private router: Router,
    private navigationService: NavigationService,
    private cartService: CartService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Initialiser les items selon l'état d'authentification
    this.updateNavItems();

    // Écouter les changements de route
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateActiveItem(event.url);
      });

    // Initialiser l'item actif
    this.updateActiveItem(this.router.url);

    // S'abonner aux changements du panier
    this.cartSubscription = this.cartService.cartItems$.subscribe(items => {
      this.cartItemCount = this.cartService.getTotalItems();
    });

    // Observer les changements d'authentification
    this.authSubscription = this.storageService.authState$.subscribe((isAuthenticated) => {
      console.log('[BottomNav] authState$ emitted:', isAuthenticated);
      this.updateNavItems();
    });
  }

  ngAfterViewInit() {
    // Calculate badge position after view is initialized
    setTimeout(() => {
      this.calculateBadgePosition();
    }, 100);
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.calculateBadgePosition();
  }

  ngOnDestroy() {
    if (this.cartSubscription) {
      this.cartSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  private updateNavItems() {
    const isAuthenticated = this.storageService.isAuthenticated();
    console.log('[BottomNav] updateNavItems called, isAuthenticated:', isAuthenticated);

    this.navItems = isAuthenticated
      ? [...this.authenticatedItems]
      : [...this.defaultItems];

    console.log('[BottomNav] navItems updated:', this.navItems.map(i => i.id));

    // Force change detection
    this.cdr.detectChanges();

    // Recalculate badge position when nav items change
    setTimeout(() => {
      this.calculateBadgePosition();
    }, 100);
  }

  private calculateBadgePosition() {
    if (!this.navWrapper || !this.bubbleBar) {
      return;
    }

    const wrapperElement = this.navWrapper.nativeElement;
    const cartElement = wrapperElement.querySelector('[data-item-id="cart"]');

    if (!cartElement) {
      return;
    }

    const wrapperRect = wrapperElement.getBoundingClientRect();
    const cartRect = cartElement.getBoundingClientRect();

    // Calculate position relative to wrapper
    const cartLeftRelative = cartRect.left - wrapperRect.left;
    const cartTopRelative = cartRect.top - wrapperRect.top;
    const cartWidth = cartRect.width;
    const cartHeight = cartRect.height;
    const badgeWidth = 24; // Approximate badge width
    const badgeHeight = 24; // Approximate badge height

    // Position badge at top-right of cart icon
    // Place the badge so its center is at the top-right edge of the icon
    const cartRightEdge = cartLeftRelative + cartWidth;
    this.badgeLeftPosition = cartRightEdge - (badgeWidth / 2);

    // Position badge vertically so it's at the top of the icon, slightly overlapping
    this.badgeTopPosition = cartTopRelative - (badgeHeight / 2);
  }

  private updateActiveItem(url: string) {
    // Nettoyer l'URL (enlever les query params et fragments)
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Mapping des sous-pages vers leurs sections parentes
    const sectionMapping: { [key: string]: string } = {
      // Section Home
      '/': 'home',
      '/home': 'home',

      // Section Products
      '/products': 'products',
      '/famille-produits': 'products',
      '/recent-donations': 'products',

      // Section Magasins
      '/magasins': 'magasins',

      // Section Cart
      '/cart': 'cart',

      // Section Profile
      '/profile': 'profile',
      '/order-history': 'profile',
      '/order-detail': 'profile',

      // Section More
      '/more': 'more',
      '/about': 'more',
      '/contact': 'more',
      '/legal-notices': 'more',
      '/cgu': 'more',

      // Section Login
      '/login': 'login',
      '/register': 'login',
      '/forgot-password': 'login',
      '/reset-password': 'login',
    };

    // Chercher une correspondance exacte d'abord
    if (sectionMapping[cleanUrl]) {
      this.activeItemId = sectionMapping[cleanUrl];
      return;
    }

    // Sinon, chercher si l'URL commence par une des clés (pour les routes avec params)
    for (const [route, section] of Object.entries(sectionMapping)) {
      if (cleanUrl.startsWith(route + '/')) {
        this.activeItemId = section;
        return;
      }
    }

    // Si aucune correspondance, chercher dans les items du nav
    const matchingItem = this.navItems.find(item => {
      const itemPath = `/${item.id}`;
      return cleanUrl === itemPath || cleanUrl.startsWith(itemPath + '/');
    });

    // Si toujours aucune correspondance, garder l'item actif actuel (ne pas changer)
    if (matchingItem) {
      this.activeItemId = matchingItem.id;
    }
    // Sinon on garde this.activeItemId tel quel
  }

  onItemClick(itemId: string) {
    const targetPath = itemId === 'home' ? '/home' : `/${itemId}`;

    // Navigation sans animation
    this.router.navigate([targetPath]);
  }

  hasCartItem(): boolean {
    return this.navItems.some(item => item.id === 'cart');
  }
}
